import type { CheckboxFilterOption } from '../checkboxFilterOption';

/**
 * AWS commercial regions, as a static list.
 *
 * `/volumes/autocomplete` has no `region` field, and deriving the list from loaded volumes
 * (as exploration does) would only ever offer regions the tenant already uses. FRNT-335 asks
 * for the full list instead, with nothing preloaded until the user searches.
 *
 * GovCloud (`us-gov-*`) and China (`cn-*`) are deliberately excluded — see the ticket.
 */
export const awsRegions: readonly string[] = [
  'af-south-1',
  'ap-east-1',
  'ap-northeast-1',
  'ap-northeast-2',
  'ap-northeast-3',
  'ap-south-1',
  'ap-south-2',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-southeast-3',
  'ap-southeast-4',
  'ap-southeast-5',
  'ap-southeast-7',
  'ca-central-1',
  'ca-west-1',
  'eu-central-1',
  'eu-central-2',
  'eu-north-1',
  'eu-south-1',
  'eu-south-2',
  'eu-west-1',
  'eu-west-2',
  'eu-west-3',
  'il-central-1',
  'me-central-1',
  'me-south-1',
  'mx-central-1',
  'sa-east-1',
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
];

/** EBS volume types, as a static list — `diskType` has no autocomplete field either. */
export const diskTypes: readonly string[] = ['gp2', 'gp3', 'io1', 'io2', 'sc1', 'sc2', 'standard'];

/** Static option lists carry no volume counts; sections render them with `showCount={false}`. */
export function toStaticOptions(values: readonly string[]): CheckboxFilterOption[] {
  return values.map((value) => ({ key: value, label: value, count: 0 }));
}

export const awsRegionOptions: CheckboxFilterOption[] = toStaticOptions(awsRegions);
export const diskTypeOptions: CheckboxFilterOption[] = toStaticOptions(diskTypes);

/**
 * Wire format for the Capacity range. Defined in `~/shared/utils/apiFilters` — where
 * `getApiFilters` consumes them — so that `shared/utils` need not import from
 * `shared/components`; re-exported here for the panel's own readers.
 */
export { diskSizeGteOp, diskSizeLteOp } from '~/shared/utils/apiFilters';

/**
 * `Volume.diskSize` is reported in BYTES, and the filter takes bytes to match.
 *
 * Confirmed from shipping UI rather than the spec: `AllVolumesTable/columnDefs.tsx` renders
 * `<SizeCell size={data.diskSize} />` WITHOUT `sizeInGiB`, i.e. via `formatBytes` — whereas
 * opt-log's `originalDiskSizeGb`/`newDiskSizeGb` opt in to GiB explicitly. The schema's
 * `Format: int32` on `diskSize` is wrong: a 100 GiB volume in bytes cannot fit an int32.
 */
export const bytesInTib = 1024 ** 4;

/** Re-exported for the panel's own readers; defined beside the capacity maths it belongs to. */
export { defaultMaxCapacityTib } from '../capacityFormat';
